"use client";

import { useState } from "react";
import Link from "next/link";
import { Crown, CalendarClock } from "lucide-react";
import { useEffectivePass } from "@/hooks/useEffectivePass";
import RandomProblems from "@/components/RandomProblems";
import RedAlert from "@/components/RedAlert";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import {
  FINAL_SESSIONS,
  defaultFinalSessionId,
  isFinalSessionAvailable,
  finalSessionComposition,
  finalSessionScopes,
} from "@/lib/final-sessions";

/** 회차별 이론/코딩 문제 수 안내. 구성이 없는(준비 중) 회차는 문제 수를 말하지 않는다. */
function compositionText(sessionId: string): string {
  const composition = finalSessionComposition(sessionId);
  if (!composition) return "프리미엄 이상 플랜에서 풀 수 있어요.";
  return `이론 ${composition.theory}문제 + 코딩 ${composition.coding}문제. 프리미엄 이상 플랜에서 풀 수 있어요.`;
}

// 토글 노출 순서: 오픈된 회차는 최신→과거(왼쪽부터), 준비 중 회차는 맨 오른쪽.
// FINAL_SESSIONS 는 정적이라 모듈 로드 시 1회만 계산한다.
const ORDERED_FINAL_SESSIONS = [
  ...FINAL_SESSIONS.filter((s) => !s.comingSoon).reverse(),
  ...FINAL_SESSIONS.filter((s) => s.comingSoon),
];

export default function FinalMockExamClient() {
  const { isLoaded, isPremiumOrAbove } = useEffectivePass();
  // 기본 회차 = 문제가 있는 가장 최신 회차 (JCG-2897). 26-2 문제가 생기면 자동으로 26-2가 기본이 됨.
  const [sessionId, setSessionId] = useState(defaultFinalSessionId());

  // 회차별 문제 구성이 달라(26-1 이론11+코딩9 / 26-2 이론10+코딩10) 안내를 회차 상태와 함께
  // 렌더한다. 로딩·페이월 단계에서는 토글이 없으므로 기본 회차 기준으로 보인다.
  const sessionAlert = (
    <RedAlert
      title="시험 직전 점검용 파이널 20문제!"
      description={compositionText(sessionId)}
      className="mt-2"
    />
  );

  if (!isLoaded) {
    return (
      <>
        {sessionAlert}
        <div className="my-10 space-y-4">
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="space-y-3">
              <Skeleton className="h-6 w-3/4" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
            </div>
          ))}
        </div>
      </>
    );
  }

  if (!isPremiumOrAbove) {
    const composition = finalSessionComposition(sessionId);
    return (
      <>
        {sessionAlert}
        <div className="my-10 overflow-hidden rounded-xl border-2 border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-900/20">
          <div className="p-6 sm:p-8 text-center">
            <div className="flex justify-center mb-4">
              <Crown className="w-12 h-12 text-orange-500" />
            </div>
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
              프리미엄 이상 플랜이 필요해요
            </h2>
            <p className="mt-3 text-sm sm:text-base text-gray-600 dark:text-gray-300">
              파이널 모의고사는 프리미엄 이상 플랜에서 풀 수 있어요.
              {composition && (
                <>
                  <br />
                  시험 직전 점검용으로 이론 {composition.theory}문제 + 코딩{" "}
                  {composition.coding}문제가 매번 랜덤으로 출제됩니다.
                </>
              )}
            </p>
            <Link
              href="/membership"
              className="mt-6 inline-flex items-center justify-center gap-2 px-6 py-3 bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-600 transition-colors"
            >
              <Crown className="w-5 h-5" />
              프리미엄 플랜 보러가기
            </Link>
          </div>
        </div>
      </>
    );
  }

  const currentSession = FINAL_SESSIONS.find((s) => s.id === sessionId);
  const available = isFinalSessionAvailable(sessionId);

  return (
    <>
      {sessionAlert}
      <div className="my-6">
        {/* 대비 회차 전환 토글 (JCG-2897) */}
        <div
          role="group"
          aria-label="대비 회차 선택"
          className="inline-flex flex-wrap gap-1 rounded-xl border border-amber-200/80 bg-white/70 p-1 shadow-sm backdrop-blur-sm dark:border-amber-900/40 dark:bg-white/5"
        >
          {ORDERED_FINAL_SESSIONS.map((s) => {
            const active = s.id === sessionId;
            // comingSoon 이면 "준비 중", 아니면 회차 강조 뱃지(UPDATE 등)를 표시
            const badgeText = s.comingSoon ? "준비 중" : s.badge;
            const isHighlightBadge = badgeText === "UPDATE" || badgeText === "NEW";
            return (
              <button
                key={s.id}
                type="button"
                aria-pressed={active}
                onClick={() => setSessionId(s.id)}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold transition-colors",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-1",
                  active
                    ? "bg-amber-500 text-white shadow"
                    : "text-gray-600 hover:bg-amber-50 dark:text-gray-300 dark:hover:bg-white/10",
                )}
              >
                {s.label}
                {badgeText && (
                  <span
                    className={cn(
                      "rounded-full px-1.5 py-0.5 text-[10px] font-bold leading-none",
                      active
                        ? "bg-white/25 text-white"
                        : isHighlightBadge
                          ? // UPDATE/NEW 강조 뱃지 — 브랜드 따뜻한 톤(orange)으로 nav UPDATE 뱃지와 통일 (기존 blue에서 변경)
                            "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300"
                          : "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
                    )}
                  >
                    {badgeText}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {available ? (
          // key로 회차 전환 시 완전 remount → 새 회차 풀에서 재출제 + 점수/진행 상태 리셋
          <RandomProblems
            key={sessionId}
            final
            finalSession={sessionId}
            scopes={finalSessionScopes(sessionId)}
            scoreTitle={
              currentSession
                ? `파이널 모의고사 · ${currentSession.label}`
                : "파이널 모의고사"
            }
          />
        ) : (
          <div className="my-8 overflow-hidden rounded-xl border-2 border-dashed border-amber-300 bg-amber-50/60 dark:border-amber-800 dark:bg-amber-900/15">
            <div className="p-6 sm:p-8 text-center">
              <div className="flex justify-center mb-4">
                <CalendarClock className="w-10 h-10 text-amber-500" />
              </div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                {currentSession?.label ?? "이 회차"} 파이널 모의고사는 준비 중이에요
              </h2>
              <p className="mt-3 text-sm sm:text-base text-gray-600 dark:text-gray-300">
                시험 직전 점검용 문제를 준비하고 있어요.{" "}
                <span className="font-semibold text-amber-600 dark:text-amber-400">
                  오픈 준비 중
                </span>
                이에요.
                <br />
                그동안 다른 회차로 미리 풀어볼 수 있어요.
              </p>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
